import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X } from "lucide-react";

// Map URL paths to human-readable labels so HubSpot shows which page
// triggered the lead.
const PATH_LABELS = {
  "/": "Home",
  "/about": "About",
  "/services": "Services",
  "/pricing": "Pricing",
  "/website-design-uk": "Website Design (UK)",
  "/ai-automation-agents-uk": "AI Agents & Automation (UK)",
  "/website-design-east-anglia": "Website Design (East Anglia)",
};

function labelForPath(pathname) {
  return PATH_LABELS[pathname] || pathname.replace(/^\//, "").replace(/-/g, " ") || "Home";
}

export default function FloatingCTA() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      // Show once the visitor has clearly scrolled past the hero section
      setVisible(window.scrollY > 600);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reset dismissal per page change so visitors can re-engage on new pages
  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  // Hide entirely on the contact page (they're already there) and privacy/terms
  if (
    location.pathname === "/contact" ||
    location.pathname === "/privacy" ||
    location.pathname === "/terms"
  ) {
    return null;
  }

  const handleClick = () => {
    const pageLabel = labelForPath(location.pathname);
    navigate("/contact#contact-form", {
      state: {
        sourcePage: pageLabel,
        sourcePath: location.pathname,
      },
    });
  };

  if (dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.95 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="fixed bottom-6 left-6 z-40 hidden sm:flex items-center"
          data-testid="floating-cta"
        >
          <button
            onClick={handleClick}
            data-testid="floating-cta-button"
            className="group relative flex items-center gap-3 pl-4 pr-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white shadow-lg shadow-cyan-500/40 hover:shadow-cyan-500/60 transition-shadow"
            aria-label="Get a quote"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
              <MessageSquare size={16} />
            </span>
            <span className="text-sm font-medium tracking-wide">Get a Quote</span>
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-400 animate-pulse" />
          </button>
          <button
            onClick={() => setDismissed(true)}
            data-testid="floating-cta-close"
            aria-label="Dismiss"
            className="ml-2 w-7 h-7 rounded-full bg-gray-800/80 hover:bg-gray-700 text-gray-300 hover:text-white flex items-center justify-center border border-gray-700"
          >
            <X size={14} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
